import React, { useMemo, useState } from 'react';
import './PlatformGapBars.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsPlatformRow } from '@brand24-original/llm-types';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { SortAltIcon } from 'primereact/icons/sortalt';
import { SortAmountDownIcon } from 'primereact/icons/sortamountdown';
import { SortAmountUpAltIcon } from 'primereact/icons/sortamountupalt';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { ALL_MODEL_DEFINITIONS } from '../../../../shared/helpers/MonitorTypeHelpers';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import GapBar from '../gap-bar/GapBar';
import GapLegend from '../gap-legend/GapLegend';

interface PlatformGapBarsProps {
  platforms: InsightsPlatformRow[] | null;
  isLoading: boolean;
  isError?: boolean;
}

interface PlatformRow {
  platform: string;
  name: string;
  icon: string;
  visibilityPct: number;
  /** null = the brand's domains are unknown (dash), distinct from a measured 0%. */
  citedPct: number | null;
  gapPp: number | null;
}

type SortKey = 'visibilityPct' | 'citedPct' | 'gapPp';
type SortDir = 'asc' | 'desc';

const SORTABLE_COLUMNS: Array<{ key: SortKey; labelKey: string }> = [
  { key: 'visibilityPct', labelKey: 'insightPage.platforms.colVisibility' },
  { key: 'citedPct', labelKey: 'insightPage.platforms.colCited' },
  { key: 'gapPp', labelKey: 'insightPage.platforms.colGap' },
];

/** Sort indicator as in CompetitorComparison: neutral double arrow, direction icon on the active column. */
const SortIndicator: React.FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => {
  if (!active) return <SortAltIcon />;
  return dir === 'asc' ? <SortAmountUpAltIcon /> : <SortAmountDownIcon />;
};

// Ta sama para (nazwa, ikona) co dropdown modeli i "Domeny według modelu" (decyzja usera).
// Legacy nazewnictwo Google: rodzina AI_MODE ma definicję pod modelKey 'AI_OVERVIEW' (nameKey
// aiModeName); AI_OVERVIEW_NEW ("AI Overview") pasuje po modelKey wprost.
const definitionForPlatform = (platform: string) => {
  const modelKey = platform === 'AI_MODE' ? 'AI_OVERVIEW' : platform;
  return ALL_MODEL_DEFINITIONS.find((def) => def.modelKey === modelKey);
};

/**
 * "Gap per AI platform" presented the same way as CompetitorComparison (user decision): a sortable grid
 * [# | platform | bar | visibility | cited | gap] with the shared `GapBar` / `GapLegend`. Unlike the
 * comparison there is NO picker — the platforms are whatever the global model filter lets the API return.
 * The table markup and sort rules are a deliberate copy of CompetitorComparison's, not a shared component
 * (user decision: leave the comparison untouched) — keep the two in step by hand.
 */
const PlatformGapBars: React.FC<PlatformGapBarsProps> = ({ platforms, isLoading, isError }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'visibilityPct',
    dir: 'desc',
  });

  // A new column starts descending (biggest first, as the default visibility order); clicking the
  // active column flips the direction.
  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
    );

  const rows = useMemo<PlatformRow[]>(() => {
    if (!platforms) return [];
    const unsorted = platforms.map((row) => {
      const definition = definitionForPlatform(row.platform);
      const visibilityPct = percentOf(row.visible, row.total);
      const citedPct = row.cited !== null ? percentOf(row.cited, row.total) : null;
      return {
        platform: row.platform,
        name: definition ? t(definition.nameKey) : row.platform,
        icon: definition?.icon ?? 'ico--globe',
        visibilityPct,
        citedPct,
        gapPp: citedPct === null ? null : visibilityPct - citedPct,
      };
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    // Unknown values sit at the BOTTOM in both directions — a fixed sentinel like -1 would jump to the
    // top of an ascending sort, above a measured 0%.
    const unknown = sort.dir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    const valueOf = (row: PlatformRow) => row[sort.key] ?? unknown;
    return unsorted.sort(
      (a, b) =>
        sign * (valueOf(a) - valueOf(b)) ||
        b.visibilityPct - a.visibilityPct ||
        a.name.localeCompare(b.name)
    );
  }, [platforms, t, sort]);

  const hasCited = rows.some((row) => row.citedPct !== null);

  const ariaSortOf = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    sort.key !== key ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending';

  return (
    <div className="stats__panel platform-gap-bars">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.platforms.title')}
          <CustomTooltip tooltipText="insightPage.platforms.tooltip" addMargin={false} />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <div className="platform-gap-bars__card">
          {isError ? (
            <EmptyPanel textKey="somethingWentWrong" />
          ) : rows.length === 0 ? (
            <EmptyPanel />
          ) : (
            <>
              <div className="platform-gap-bars__table" role="table">
                <div className="platform-gap-bars__header" role="row">
                  <span role="columnheader">#</span>
                  <span role="columnheader">{t('insightPage.platforms.colPlatform')}</span>
                  <span role="columnheader" />
                  {SORTABLE_COLUMNS.map(({ key, labelKey }) => (
                    <span key={key} role="columnheader" aria-sort={ariaSortOf(key)}>
                      <Button
                        type="button"
                        className="btn btn--link platform-gap-bars__sort"
                        onClick={() => toggleSort(key)}
                      >
                        {t(labelKey)}
                        <SortIndicator active={sort.key === key} dir={sort.dir} />
                      </Button>
                    </span>
                  ))}
                </div>
                {rows.map((row, index) => (
                  <div key={row.platform} role="row" className="platform-gap-bars__row">
                    <span role="cell" className="platform-gap-bars__rank">
                      {index + 1}
                    </span>
                    <span role="cell" className="platform-gap-bars__model">
                      <span className={`ico--sm ${row.icon}`} />
                      <span className="platform-gap-bars__name" title={row.name}>
                        {row.name}
                      </span>
                    </span>
                    <span role="cell">
                      <GapBar visibilityPct={row.visibilityPct} citedPct={row.citedPct} />
                    </span>
                    <span role="cell" className="platform-gap-bars__value" data-label={t('insightPage.platforms.colVisibility')}>
                      {row.visibilityPct}%
                    </span>
                    <span role="cell" className="platform-gap-bars__value" data-label={t('insightPage.platforms.colCited')}>
                      {row.citedPct !== null ? `${row.citedPct}%` : '—'}
                    </span>
                    <span role="cell" className="platform-gap-bars__value platform-gap-bars__value--gap" data-label={t('insightPage.platforms.colGap')}>
                      {row.gapPp !== null
                        ? t('insightPage.platforms.gapValue', { value: row.gapPp })
                        : '—'}
                    </span>
                  </div>
                ))}
              </div>
              <GapLegend
                visibilityLabel={t('insightPage.platforms.legendVisibility')}
                citedLabel={hasCited ? t('insightPage.platforms.legendCited') : null}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PlatformGapBars;
